import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { bem } from 'helpers/class-name';
import { useIntl } from 'react-intl';
import { makeValidators } from '../helpers';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

import './Tags.scss';

/*
  <Tags> and <Tag> work together to recursively display nested checkboxes as a single RHF field.

  Props:
    tags - the nested list of tags and subtags
    name - RHF field name
    onChange - passed the checkbox's value and checked as well as the current field value - return updated field value
    subtagKey - tags[i][subtagKey] === recusively nested tags
    isChecked - a function that receives the current field value and a tag - return true/false

  Example:
    // tagIds = ['1', '1-2', '3']
    <Tags
      tags={[
        {
          id: '1',
          name: 'one',
          subtags: [
            { id: '1-1', name: 'one-one' },
            { id: '1-2', name: 'one-two' },
          ],
        },
        { id: '2', name: 'two' },
        { id: '3', name: 'three' },
      ]}
      name="tagIds"
      subtagKey="subtags"
      isChecked={(fieldValue, { id }) => fieldValue.includes(id)}
      onChange={(value, checked, tagIds) => {
        return !checked && tagIds.includes(value)
          ? tagIds.filter(id => id !== value)
          : tagIds.concat(value)}
      }
    >
      {({ id, name }) => <Tag value={id} label={`${name} ${id}`} />}
    </Tags>
*/

const { block, element } = bem('hook-form-tags');

function Tags(props) {
  const {
    name,
    values,
    rhfOnChange,
    handleOnChange,
    tags,
    subtagKey = 'subtags',
    isChecked = (fieldValue, { id }) => fieldValue.includes(id),
    className,
    children,
  } = props;
  const handleChange = event => {
    const { value, checked } = event.target;
    return rhfOnChange(handleOnChange(value, checked, values));
  };
  return (
    <ul className={block(className)}>
      {tags.map(tag => {
        const subtags = tag[subtagKey] || [];
        const checked = isChecked(values, tag);
        const child = children(tag, checked);

        return (
          <li key={JSON.stringify(tag)} className={element('tag')}>
            {child !== null ? React.cloneElement(child, { checked, name, handleChange }) : null}
            {subtags.length > 0 && (
              <Tags
                name={name}
                values={values}
                rhfOnChange={rhfOnChange}
                handleOnChange={handleOnChange}
                tags={subtags}
                subtagKey={subtagKey}
                className={element('subtags', !checked ? 'hidden' : '')}>
                {children}
              </Tags>
            )}
          </li>
        );
      })}
    </ul>
  );
}

Tags.propTypes = {
  children: PropTypes.func,
  className: PropTypes.string,
  handleOnChange: PropTypes.func,
  isChecked: PropTypes.func,
  name: PropTypes.string,
  rhfOnChange: PropTypes.func,
  subtagKey: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})])),
};

function TagsWrapper(props) {
  const { name, onChange: handleOnChange, defaultValues, children, ...restProps } = props;
  const {
    control,

    formState: { errors },
  } = useFormContext();
  const intl = useIntl();

  const validators = makeValidators(props, intl);

  return (
    <>
      <Controller
        rules={validators}
        control={control}
        defaultValue={defaultValues}
        name={name}
        render={({ field: { onChange, value } }) => {
          return (
            <Tags rhfOnChange={onChange} handleOnChange={handleOnChange} values={value} {...restProps}>
              {children}
            </Tags>
          );
        }}
      />
      <ErrorMessage name={name} errors={errors} />
    </>
  );
}

TagsWrapper.propTypes = {
  defaultValues: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({})])),
  children: PropTypes.func,
  onChange: PropTypes.func,
  name: PropTypes.string,
};

export default TagsWrapper;
